/**
 * OTC Exchange Hook
 *
 * Extracts code from query params and exchanges it for tokens.
 * Automatically redirects on success or shows error on failure.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { LoginResponse } from "@its-engineering-and-development/jwt-auth-client";
import { authClient } from "@/api/auth-client";
import { expenseAdminTokenSource } from "@/auth";
import { useAppDispatch } from "@/store";
import { setCredentials } from "@/store/auth.slice";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";

export interface UseOtcExchangeOptions {
    redirectPath?: string;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

export function useOtcExchange(options?: UseOtcExchangeOptions) {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showSuccess } = useNotifications();
    const code = searchParams.get("code");
    const hasExecutedRef = useRef(false);
    const [state, setState] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [error, setError] = useState<Error | null>(null);

    const execute = useCallback(
        async ({ code }: { code: string }) => {
            const clientId = import.meta.env.VITE_EXPENSE_ADMIN_CLIENT_ID?.trim();

            if (!clientId) {
                const configError = new Error("VITE_EXPENSE_ADMIN_CLIENT_ID is not configured");
                setError(configError);
                setState("error");
                options?.onError?.(configError);
                navigate("/login", { replace: true });
                throw configError;
            }

            setState("loading");
            setError(null);

            try {
                const response = (await authClient.exchangeOneTimeCode(code, clientId)) as LoginResponse;

                authClient.setTokens(response.accessToken, response.refreshToken);
                dispatch(setCredentials({ accessToken: response.accessToken }));
                // Re-resolve permissions immediately so guards show the loader
                // instead of flashing "access denied" during the poll interval.
                expenseAdminTokenSource.notifyTokenChange();
                setState("success");

                showSuccess("Authentication successful!", "You have been logged in.");
                options?.onSuccess?.();
                navigate(options?.redirectPath ?? "/", { replace: true });

                return response;
            } catch (err) {
                const authError = err instanceof Error ? err : new Error(String(err));
                setError(authError);
                setState("error");
                options?.onError?.(authError);
                navigate("/login", { replace: true });
                throw authError;
            }
        },
        [dispatch, navigate, options, showSuccess],
    );

    useEffect(() => {
        // Only execute once per code
        if (hasExecutedRef.current) return;

        if (code && state === "idle") {
            hasExecutedRef.current = true;
            void execute({ code }).catch(() => undefined);
        } else if (!code && state === "idle") {
            hasExecutedRef.current = true;
            navigate("/login", { replace: true });
        }
    }, [code, state, execute, navigate]);

    return { state, error, isLoading: state === "loading", isError: state === "error" };
}
